import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, CreditCard, Banknote, MapPin, Phone, User, ChevronLeft, Check, Shield, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { checkoutId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [branch, setBranch] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState('');
  const [formData, setFormData] = useState({ customer_name: '', customer_phone: '', customer_address: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [hyperpayStep, setHyperpayStep] = useState(false);

  useEffect(() => { fetchCheckout(); }, [checkoutId]);

  const fetchCheckout = async () => {
    try {
      const res = await axios.get(`${API}/public/checkout/${checkoutId}`);
      setSession(res.data.session);
      setBranch(res.data.branch);
      setPaymentMethods(res.data.payment_methods);
      setAlreadyPaid(res.data.already_paid);
      if (res.data.session?.customer_name) setFormData(prev => ({ ...prev, customer_name: res.data.session.customer_name }));
      if (res.data.session?.customer_phone) setFormData(prev => ({ ...prev, customer_phone: res.data.session.customer_phone }));
      if (res.data.session?.customer_address) setFormData(prev => ({ ...prev, customer_address: res.data.session.customer_address }));
    } catch {
      setSession(null);
    } finally { setLoading(false); }
  };

  const validate = () => {
    const e = {};
    if (!formData.customer_name.trim()) e.customer_name = 'الاسم مطلوب';
    if (!formData.customer_phone.trim()) e.customer_phone = 'رقم الجوال مطلوب';
    else if (!/^(05|5|\+9665|009665)\d{7,8}$/.test(formData.customer_phone.replace(/\s/g, ''))) e.customer_phone = 'رقم جوال غير صحيح';
    if (!selectedPayment) e.payment = 'اختر طريقة الدفع';
    if (!session?.is_dine_in && selectedPayment === 'cod' && !formData.customer_address.trim()) e.customer_address = 'العنوان مطلوب للتوصيل';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (selectedPayment === 'hyperpay') {
        const res = await axios.post(`${API}/public/checkout/confirm`, {
          checkout_id: checkoutId, payment_method: 'hyperpay',
          customer_name: formData.customer_name, customer_phone: formData.customer_phone,
          customer_address: formData.customer_address, notes: formData.notes,
        });
        if (res.data.success) {
          setHyperpayStep(true);
        }
      } else {
        const res = await axios.post(`${API}/public/checkout/confirm`, {
          checkout_id: checkoutId, payment_method: 'cod',
          customer_name: formData.customer_name, customer_phone: formData.customer_phone,
          customer_address: formData.customer_address, notes: formData.notes,
        });
        if (res.data.success) {
          setOrderResult(res.data);
        }
      }
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'حصل خطأ. حاول مرة ثانية' });
    } finally { setSubmitting(false); }
  };

  const handleSimulatePayment = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/checkout/hyperpay-callback`, {
        checkout_id: checkoutId, payment_id: `test_${Date.now()}`, result_code: '000.000.000', result_description: 'Success',
      });
      if (res.data.success) {
        setOrderResult(res.data);
        setHyperpayStep(false);
      }
    } catch (err) {
      setErrors({ submit: 'فشل الدفع' });
    } finally { setSubmitting(false); }
  };

  const primaryColor = '#D97706';

  if (loading) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#08080D', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader2 style={{ width: 32, height: 32, color: primaryColor, animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!session) return (
    <div dir="rtl" style={{ minHeight: '100vh', background: '#08080D', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F0F0F5', fontFamily: 'Tajawal, sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>جلسة الدفع غير موجودة</h2>
        <p style={{ color: '#6B7280', fontSize: 14 }}>ربما انتهت صلاحيتها. عد للمحادثة وأعد الطلب</p>
      </div>
    </div>
  );

  // Order confirmed view
  if (orderResult) return (
    <div dir="rtl" data-testid="checkout-success" style={{ minHeight: '100vh', background: '#08080D', fontFamily: 'Tajawal, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#10B98120', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Check style={{ width: 40, height: 40, color: '#10B981' }} />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#F0F0F5', marginBottom: 8 }}>تم تأكيد الطلب!</h1>
        <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 24 }}>{orderResult.message}</p>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ color: '#6B7280', fontSize: 13 }}>رقم الطلب</span>
            <span style={{ color: '#F0F0F5', fontWeight: 700, fontSize: 13 }}>{orderResult.order_id?.slice(0, 8)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ color: '#6B7280', fontSize: 13 }}>الإجمالي</span>
            <span style={{ color: primaryColor, fontWeight: 800, fontSize: 16 }}>{orderResult.total} ر.س</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6B7280', fontSize: 13 }}>طريقة الدفع</span>
            <span style={{ color: '#F0F0F5', fontWeight: 600, fontSize: 13 }}>{orderResult.payment_method === 'cod' ? 'الدفع عند الاستلام' : 'تم الدفع إلكترونياً'}</span>
          </div>
        </div>
        {orderResult.tracking_url && (
          <a href={orderResult.tracking_url} data-testid="track-order-btn" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 32px', borderRadius: 24,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)`, color: '#fff',
            fontWeight: 700, fontSize: 14, textDecoration: 'none', boxShadow: `0 4px 20px ${primaryColor}40`,
          }}>
            تتبع الطلب 📦
          </a>
        )}
      </div>
    </div>
  );

  // HyperPay test mode simulation
  if (hyperpayStep) return (
    <div dir="rtl" data-testid="hyperpay-test-page" style={{ minHeight: '100vh', background: '#08080D', fontFamily: 'Tajawal, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 20, padding: 28, border: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20, background: '#F59E0B20', border: '1px solid #F59E0B40', marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B' }}>وضع تجريبي TEST MODE</span>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#F0F0F5', marginBottom: 8 }}>بوابة الدفع HyperPay</h2>
            <p style={{ color: '#6B7280', fontSize: 13 }}>في الإنتاج، ستظهر هنا نافذة HyperPay الحقيقية</p>
          </div>

          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>المبلغ المطلوب</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: primaryColor }}>{session?.total} ر.س</div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            {['MADA', 'VISA', 'MASTER', 'Apple Pay'].map(brand => (
              <div key={brand} style={{ padding: '8px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: 11, fontWeight: 700, color: '#9CA3AF' }}>
                {brand}
              </div>
            ))}
          </div>

          {/* Simulated card fields */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>رقم البطاقة</label>
            <input disabled placeholder="4111 1111 1111 1111" data-testid="hp-card-number" style={{
              width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#6B7280', fontSize: 14, fontFamily: 'monospace', outline: 'none',
            }} />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>تاريخ الانتهاء</label>
              <input disabled placeholder="12/28" style={{
                width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                color: '#6B7280', fontSize: 14, outline: 'none',
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>CVV</label>
              <input disabled placeholder="123" style={{
                width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                color: '#6B7280', fontSize: 14, outline: 'none',
              }} />
            </div>
          </div>

          <button onClick={handleSimulatePayment} disabled={submitting} data-testid="simulate-pay-btn" style={{
            width: '100%', padding: '14px', borderRadius: 16, background: `linear-gradient(135deg, #10B981, #059669)`,
            color: '#fff', border: 'none', cursor: submitting ? 'wait' : 'pointer', fontWeight: 700, fontSize: 15,
            fontFamily: 'Tajawal, sans-serif', boxShadow: '0 4px 20px rgba(16,185,129,0.3)', transition: 'all 0.25s',
            opacity: submitting ? 0.6 : 1,
          }}>
            {submitting ? 'جاري المعالجة...' : `ادفع ${session?.total} ر.س (تجريبي)`}
          </button>

          <button onClick={() => setHyperpayStep(false)} style={{
            width: '100%', padding: '12px', borderRadius: 12, background: 'transparent',
            color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', marginTop: 10,
            fontWeight: 600, fontSize: 13, fontFamily: 'Tajawal, sans-serif',
          }}>
            رجوع
          </button>

          <div style={{ textAlign: 'center', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Shield style={{ width: 12, height: 12, color: '#6B7280' }} />
            <span style={{ fontSize: 11, color: '#6B7280' }}>مشفر ومحمي بمعايير PCI-DSS</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Main checkout form
  return (
    <div dir="rtl" data-testid="checkout-page" style={{ minHeight: '100vh', background: '#08080D', fontFamily: 'Tajawal, sans-serif', padding: '0 0 40px' }}>
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        .fade-in{animation:fadeIn 0.4s ease both}
        input::placeholder{color:#4B5563}
      `}</style>

      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(8,8,13,0.95)', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 520, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => window.history.back()} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 8, cursor: 'pointer', display: 'flex' }}>
            <ChevronLeft style={{ width: 18, height: 18, color: '#9CA3AF', transform: 'scaleX(-1)' }} />
          </button>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: '#F0F0F5', margin: 0 }}>تأكيد الطلب</h1>
            {branch?.name && <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0' }}>{branch.name}</p>}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '20px 16px' }}>
        {/* Already paid */}
        {alreadyPaid && (
          <div className="fade-in" style={{ textAlign: 'center', padding: 40 }}>
            <Check style={{ width: 48, height: 48, color: '#10B981', margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#F0F0F5' }}>تم الدفع مسبقاً</h2>
          </div>
        )}

        {!alreadyPaid && (
          <>
            {/* Order Summary */}
            <div className="fade-in" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <ShoppingCart style={{ width: 16, height: 16, color: primaryColor }} />
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#F0F0F5', margin: 0 }}>ملخص الطلب</h2>
                <span style={{ fontSize: 12, color: '#6B7280' }}>({session?.items?.length} أصناف)</span>
              </div>
              {session?.items?.map((item, i) => (
                <div key={i} data-testid={`checkout-item-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < session.items.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  {item.image_url && (
                    <img src={item.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                      alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', background: 'rgba(255,255,255,0.05)' }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#F0F0F5' }}>{item.name_ar || item.name}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>x{item.quantity}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: primaryColor }}>{(item.price * item.quantity).toFixed(0)} ر.س</div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#6B7280' }}>المجموع</span>
                  <span style={{ fontSize: 13, color: '#9CA3AF' }}>{session?.subtotal} ر.س</span>
                </div>
                {session?.delivery_fee > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: '#6B7280' }}>رسوم التوصيل</span>
                    <span style={{ fontSize: 13, color: '#9CA3AF' }}>{session?.delivery_fee} ر.س</span>
                  </div>
                )}
                {session?.is_dine_in && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: '#6B7280' }}>نوع الطلب</span>
                    <span style={{ fontSize: 13, color: '#10B981', fontWeight: 600 }}>🪑 أكل في المطعم</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#F0F0F5' }}>الإجمالي</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: primaryColor }}>{session?.total} ر.س</span>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="fade-in" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 16, animationDelay: '0.1s' }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#F0F0F5', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <User style={{ width: 16, height: 16, color: primaryColor }} /> بيانات العميل
              </h2>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>الاسم *</label>
                <input value={formData.customer_name} onChange={e => setFormData(p => ({ ...p, customer_name: e.target.value }))}
                  placeholder="اسمك الكريم" data-testid="checkout-name-input"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: `1px solid ${errors.customer_name ? '#EF4444' : 'rgba(255,255,255,0.1)'}`, color: '#F0F0F5', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none', transition: 'border 0.2s' }}
                  onFocus={e => e.target.style.borderColor = `${primaryColor}60`} onBlur={e => e.target.style.borderColor = errors.customer_name ? '#EF4444' : 'rgba(255,255,255,0.1)'}
                />
                {errors.customer_name && <span style={{ fontSize: 11, color: '#EF4444', marginTop: 4, display: 'block' }}>{errors.customer_name}</span>}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>رقم الجوال *</label>
                <input value={formData.customer_phone} onChange={e => setFormData(p => ({ ...p, customer_phone: e.target.value }))}
                  placeholder="05XXXXXXXX" data-testid="checkout-phone-input" dir="ltr"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: `1px solid ${errors.customer_phone ? '#EF4444' : 'rgba(255,255,255,0.1)'}`, color: '#F0F0F5', fontSize: 14, fontFamily: 'monospace', outline: 'none', textAlign: 'left' }}
                />
                {errors.customer_phone && <span style={{ fontSize: 11, color: '#EF4444', marginTop: 4, display: 'block' }}>{errors.customer_phone}</span>}
              </div>
              {!session?.is_dine_in && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>عنوان التوصيل {selectedPayment === 'cod' ? '*' : ''}</label>
                  <input value={formData.customer_address} onChange={e => setFormData(p => ({ ...p, customer_address: e.target.value }))}
                    placeholder="الحي، الشارع، رقم المبنى" data-testid="checkout-address-input"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: `1px solid ${errors.customer_address ? '#EF4444' : 'rgba(255,255,255,0.1)'}`, color: '#F0F0F5', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none' }}
                  />
                  {errors.customer_address && <span style={{ fontSize: 11, color: '#EF4444', marginTop: 4, display: 'block' }}>{errors.customer_address}</span>}
                </div>
              )}
              <div>
                <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 6, display: 'block' }}>ملاحظات (اختياري)</label>
                <input value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="بدون بصل، حار زيادة..." data-testid="checkout-notes-input"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F0F5', fontSize: 14, fontFamily: 'Tajawal, sans-serif', outline: 'none' }}
                />
              </div>
            </div>

            {/* Payment Methods */}
            <div className="fade-in" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 20, animationDelay: '0.2s' }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#F0F0F5', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CreditCard style={{ width: 16, height: 16, color: primaryColor }} /> طريقة الدفع
              </h2>
              {errors.payment && <span style={{ fontSize: 12, color: '#EF4444', marginBottom: 10, display: 'block' }}>{errors.payment}</span>}

              {/* HyperPay option */}
              {paymentMethods?.hyperpay?.enabled && (
                <button onClick={() => setSelectedPayment('hyperpay')} data-testid="payment-hyperpay"
                  style={{
                    width: '100%', padding: '16px', borderRadius: 14, marginBottom: 10, cursor: 'pointer',
                    background: selectedPayment === 'hyperpay' ? `${primaryColor}12` : 'rgba(255,255,255,0.02)',
                    border: `2px solid ${selectedPayment === 'hyperpay' ? primaryColor : 'rgba(255,255,255,0.08)'}`,
                    transition: 'all 0.25s', textAlign: 'right',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: selectedPayment === 'hyperpay' ? `${primaryColor}20` : 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CreditCard style={{ width: 20, height: 20, color: selectedPayment === 'hyperpay' ? primaryColor : '#6B7280' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: selectedPayment === 'hyperpay' ? '#F0F0F5' : '#9CA3AF' }}>دفع إلكتروني</div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>مدى • فيزا • ماستركارد • Apple Pay</div>
                    </div>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${selectedPayment === 'hyperpay' ? primaryColor : 'rgba(255,255,255,0.15)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selectedPayment === 'hyperpay' && <div style={{ width: 10, height: 10, borderRadius: '50%', background: primaryColor }} />}
                    </div>
                  </div>
                  {paymentMethods.hyperpay.test_mode && (
                    <div style={{ marginTop: 8, padding: '4px 10px', borderRadius: 8, background: '#F59E0B15', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#F59E0B' }}>وضع تجريبي</span>
                    </div>
                  )}
                </button>
              )}

              {/* COD option */}
              {paymentMethods?.cod?.enabled && (
                <button onClick={() => setSelectedPayment('cod')} data-testid="payment-cod"
                  style={{
                    width: '100%', padding: '16px', borderRadius: 14, cursor: 'pointer',
                    background: selectedPayment === 'cod' ? `${primaryColor}12` : 'rgba(255,255,255,0.02)',
                    border: `2px solid ${selectedPayment === 'cod' ? primaryColor : 'rgba(255,255,255,0.08)'}`,
                    transition: 'all 0.25s', textAlign: 'right',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: selectedPayment === 'cod' ? `${primaryColor}20` : 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Banknote style={{ width: 20, height: 20, color: selectedPayment === 'cod' ? primaryColor : '#6B7280' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: selectedPayment === 'cod' ? '#F0F0F5' : '#9CA3AF' }}>الدفع عند الاستلام</div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>ادفع نقداً عند استلام الطلب</div>
                    </div>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${selectedPayment === 'cod' ? primaryColor : 'rgba(255,255,255,0.15)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selectedPayment === 'cod' && <div style={{ width: 10, height: 10, borderRadius: '50%', background: primaryColor }} />}
                    </div>
                  </div>
                </button>
              )}
            </div>

            {/* Submit */}
            {errors.submit && (
              <div style={{ padding: '10px 16px', borderRadius: 10, background: '#EF444420', border: '1px solid #EF444440', marginBottom: 16, fontSize: 13, color: '#EF4444', fontWeight: 600 }}>
                {errors.submit}
              </div>
            )}

            <button onClick={handleSubmit} disabled={submitting} data-testid="confirm-order-btn" style={{
              width: '100%', padding: '16px', borderRadius: 16,
              background: selectedPayment ? `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)` : 'rgba(255,255,255,0.06)',
              color: selectedPayment ? '#fff' : '#4B5563', border: 'none', cursor: submitting ? 'wait' : selectedPayment ? 'pointer' : 'default',
              fontWeight: 800, fontSize: 16, fontFamily: 'Tajawal, sans-serif',
              boxShadow: selectedPayment ? `0 4px 24px ${primaryColor}30` : 'none',
              opacity: submitting ? 0.6 : 1, transition: 'all 0.3s',
            }}>
              {submitting ? 'جاري التأكيد...' : selectedPayment === 'hyperpay' ? `ادفع ${session?.total} ر.س` : selectedPayment === 'cod' ? 'تأكيد الطلب (الدفع عند الاستلام)' : 'اختر طريقة الدفع'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Shield style={{ width: 13, height: 13, color: '#4B5563' }} />
              <span style={{ fontSize: 11, color: '#4B5563' }}>بياناتك محمية ومشفرة</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
